import api from './api';

/**
 * Announcement Service
 *
 * User-facing API for fetching and interacting with popup announcements.
 * Called by AnnouncementProvider to display modals after login.
 */

export interface AnnouncementPopup {
  logId: number;
  messageId: number;
  trigger: string;
  title: string;
  content: string;
  popupConfig: {
    ctaText?: string;
    ctaUrl?: string;
    secondaryText?: string;
    style?: string;
    icon?: 'rocket' | 'bell' | 'gift' | 'star' | 'clock' | 'refresh' | 'credit-card';
    dismissable?: boolean;
    maxShowCount?: number;
    imageUrl?: string;
    pages?: Array<{ title: string; content: string; imageUrl?: string; ctaOverride?: { text: string; url: string } }>;
    coverType?: 'icon' | 'image';
    showBackButton?: boolean;
  };
  priority: number;
}

class AnnouncementService {
  /**
   * Get pending popup announcements for the current user.
   */
  async getPendingPopups(): Promise<AnnouncementPopup[]> {
    const response = await api.get('/announcements');
    return response.data || [];
  }

  /**
   * Mark a popup as displayed.
   */
  async markDisplayed(messageId: number): Promise<void> {
    await api.post(`/announcements/${messageId}/interact`, { action: 'displayed' });
  }

  /**
   * Mark a popup as dismissed.
   */
  async markDismissed(messageId: number): Promise<void> {
    await api.post(`/announcements/${messageId}/interact`, { action: 'dismissed' });
  }

  /**
   * Mark a popup as clicked (CTA).
   */
  async markClicked(messageId: number): Promise<void> {
    await api.post(`/announcements/${messageId}/interact`, { action: 'clicked' });
  }
}

const announcementService = new AnnouncementService();
export default announcementService;
